/** "Submit to review" button + modal (was: Escalate).
 *
 * Posts to /api/v1/jobs/{job_id}/stages/{stage}/escalate (endpoint name
 * kept for back-compat) which evaluates the tenant's escalation_routes
 * and routes the stage to a senior reviewer. The reviewer's comment is
 * the only required input; the agent's summary is pre-filled from the
 * current output so the recipient sees what triggered it.
 *
 * UI labels renamed to "Submit to review" — "Escalate" implied something
 * is broken, but the actual use is "hand this stage to a senior
 * reviewer for sign-off on an edge case", which is review routing,
 * not error escalation.
 */
import { useState } from 'react';
import { Send, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { api, ApiError } from '@/lib/api';
import type { PausedStage } from '@/types/enums';

interface Props {
  jobId: string;
  stage: PausedStage;
  /** A short text summary of the agent's output to send to the recipient. */
  agentSummary?: string;
  disabled?: boolean;
  onEscalated?: () => void;
}

export function EscalateButton({ jobId, stage, agentSummary, disabled, onEscalated }: Props) {
  const [open, setOpen] = useState(false);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  function close() {
    setOpen(false);
    setComment('');
    setError(null);
    setSuccess(null);
  }

  async function submit() {
    if (!comment.trim()) {
      setError('Please add a comment so the recipient understands what to do.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await api.post(`/api/v1/jobs/${jobId}/stages/${stage}/escalate`, {
        comment: comment.trim(),
        agent_summary: agentSummary?.trim() || null,
      });
      setSuccess('Submitted for review. Recipients will see it in My queue.');
      onEscalated?.();
      setTimeout(close, 1500);
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : 'Escalation failed. Please retry.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <Button
        variant="outline"
        disabled={disabled}
        onClick={() => { setOpen(true); }}
        className="gap-1.5 border-pink-300 text-pink-800 hover:bg-pink-50 dark:border-pink-700 dark:text-pink-300"
        title="Submit this stage to a senior reviewer"
      >
        <Send className="h-4 w-4" aria-hidden="true" />
        Submit to review
      </Button>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Submit stage to review"
          className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm"
          onClick={(e) => { if (e.target === e.currentTarget) close(); }}
        >
          <div className="bg-card rounded-lg border shadow-lg max-w-lg w-full mx-4 p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold flex items-center gap-2">
                <Send className="h-4 w-4 text-pink-600" aria-hidden="true" />
                Submit this stage to review
              </h2>
              <button onClick={close} aria-label="Close" className="text-muted-foreground hover:text-foreground">
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              The backend will look up your tenant's escalation routes, pick the
              matching one(s), and create a task for each listed recipient. The
              task will show up in their <em>My escalations</em> queue. If no
              route matches, the task is sent to all admins as a fallback.
            </p>
            <label className="block">
              <span className="text-xs font-medium text-muted-foreground">Comment for the recipient</span>
              <textarea
                value={comment}
                onChange={(e) => { setComment(e.target.value); }}
                rows={4}
                placeholder="Why are you escalating? What should the recipient check or decide?"
                className="mt-1 w-full text-sm rounded border px-2 py-1.5 bg-background"
                autoFocus
              />
            </label>
            {agentSummary && (
              <details className="text-xs">
                <summary className="cursor-pointer text-muted-foreground">
                  Agent summary that will be sent
                </summary>
                <p className="mt-1 text-muted-foreground whitespace-pre-wrap border-l-2 pl-2 max-h-40 overflow-auto">
                  {agentSummary}
                </p>
              </details>
            )}
            {error && (
              <Alert variant="destructive">
                <AlertDescription className="text-xs">{error}</AlertDescription>
              </Alert>
            )}
            {success && (
              <Alert>
                <AlertDescription className="text-xs">{success}</AlertDescription>
              </Alert>
            )}
            <div className="flex items-center justify-end gap-2 pt-1">
              <Button variant="ghost" onClick={close} disabled={submitting}>
                Cancel
              </Button>
              <Button
                onClick={() => { void submit(); }}
                disabled={submitting || !comment.trim()}
                className="gap-1.5 bg-pink-600 hover:bg-pink-700 text-white"
              >
                <Send className="h-4 w-4" aria-hidden="true" />
                Submit
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
